import React from "react";
import clsx from "clsx";

import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import List from "components/List";
import PastPerformances from "components/PastPerformances";
import TabTables from "components/TabTables";
import { useRacehorse360Api } from "hooks/api";
import { isFutureDate, transformViewDate } from "utils/date-utils";
import ActivityComponentType from "interfaces/ActivityComponentType";
import IColumn from "interfaces/Column";
import { SortOrder } from "interfaces/SortOrder";
import Workouts from "./Workouts";

import useStyles from "./styles";

export interface Props {
  horse: racehorse360.IHorse;
  rootRef: any;
}

const Activity = (props: Props) => {
  const { horse, rootRef } = props;

  const classes = useStyles();
  const theme = useTheme();
  const matchesUpMd = useMediaQuery(theme.breakpoints.up("md"), {
    noSsr: true
  });

  const horseOnListsColumns: IColumn[] = [
    {
      id: "ListType",
      title: "List Type",
      field: "listType",
      width: "40%"
    },
    {
      id: "Track",
      title: "Track",
      field: "facility.code",
      width: "20%"
    },
    {
      id: "DateOn",
      title: "Date On",
      field: "dateOn",
      width: "20%",
      renderer: val => (val ? transformViewDate(val) : "-")
    },
    {
      id: "DateOff",
      title: "Date Off",
      field: "dateOff",
      width: "20%",
      renderer: val =>
        val ? (
          <span className={clsx({ [classes.italicCell]: isFutureDate(val) })}>
            {transformViewDate(val)}
          </span>
        ) : (
          "-"
        )
    }
  ];

  const { useListWorkouts, useListHorseOnLists } = useRacehorse360Api();

  const { isLoading: isLoadingWorkouts, data: workoutsData } = useListWorkouts(
    {
      query: {
        horseIds: [horse.id]
      },
      pagingOptions: {
        maxResults: 999,
        includeSummary: true
      },
      getOptions: {
        select: [
          "id",
          "date",
          "facility.code",
          "comment",
          "courseSurface",
          "distance",
          "distanceUnit",
          "workoutType",
          "timing",
          "ranking",
          "numberInRankingGroup",
          "trackCondition",
          "gateIndicator",
          "dogIndicator"
        ],
        orderBy: [`date ${SortOrder.DESC}`]
      }
    },
    {
      onError: error => console.error(error)
    }
  );

  const workouts = workoutsData?.workouts || [];

  const renderListActivity = (
    tabName: string,
    component: ActivityComponentType
  ) => (
    <List<
      racehorse360.IHorse,
      racehorse360.IHorseOnList,
      racehorse360.IListHorseOnListsRequest,
      racehorse360.IListHorseOnListsResponse
    >
      entity={horse}
      apiListHook={useListHorseOnLists}
      query={{ horseId: { value: horse?.id } }}
      getOptions={{
        select: [
          "list_type",
          "facility.code",
          "date_on",
          "date_off",
          "reason",
          "comment"
        ],
        orderBy: [`dateOff ${SortOrder.DESC}`, `dateOn ${SortOrder.DESC}`]
      }}
      listPropName="horseOnLists"
      tabName={tabName}
      columns={horseOnListsColumns}
      rootRef={rootRef}
      component={component}
      isAlertItemPredicate={(item: racehorse360.IHorseOnList) =>
        !item.dateOff || isFutureDate(item.dateOff)
      }
      commentsSource={["reason", "comment"]}
    />
  );

  const renderTabListsDesktop = () => (
    <TabTables
      defaultOpen={1}
      tabs={[
        {
          name: "Workouts",
          component: (
            <Workouts
              horse={horse}
              pagination
              rootRef={rootRef}
              workouts={workouts}
              isLoading={isLoadingWorkouts}
            />
          )
        },
        {
          name: "PPs",
          component: (
            <PastPerformances horse={horse} pagination rootRef={rootRef} />
          )
        },
        {
          name: "Lists",
          component: renderListActivity(
            "Lists",
            ActivityComponentType.PAGINATED
          )
        }
      ]}
    />
  );

  const renderTabLists = () => (
    <>
      <section className={classes.section}>
        <header className={classes.header}>Lists and Risks</header>

        <section className={classes.content}>
          {renderListActivity("List Activity", ActivityComponentType.LIST)}
        </section>
      </section>

      <section className={classes.section}>
        <header className={classes.header}>Workouts and PPs</header>

        <section className={classes.content}>
          <Workouts
            horse={horse}
            rootRef={rootRef}
            workouts={workouts}
            isLoading={isLoadingWorkouts}
          />
        </section>

        <section className={classes.content}>
          <PastPerformances
            horse={horse}
            rootRef={rootRef}
            maxResults={matchesUpMd ? 3 : 99}
          />
        </section>
      </section>
    </>
  );

  return matchesUpMd ? renderTabListsDesktop() : renderTabLists();
};

export default React.memo(Activity);
